import { YES_NO } from "../constants"
import {
  constructionStatusOptions,
  occupancyStatusOptions,
  buildingPlotAreaUnitOptions,
} from "../sharedOptions"
import type { CategorySection } from "../types"

export const hospitalitySections: Record<string, CategorySection[]> = {
  "Hospitality (Operational)": [
    {
      sectionTitle: "Hotel Profile & Asset Details",
      fields: [
        { key: "hotelType", type: "text", label: "Hotel Type", placeholder: "" },
        { key: "isHotelBrandAffiliated", type: "yesno", label: "Is the hotel brand affiliated?", options: YES_NO },
        { key: "brandName", type: "text", label: "Brand Name", placeholder: "" },
        { key: "starRating", type: "text", label: "Star Rating", placeholder: "" },
        {
          key: "plotAreaUnit",
          type: "dropdown",
          label: "Plot Area Unit",
          options: buildingPlotAreaUnitOptions,
          placeholder: "Unit",
          tooltip: "Default Sq. Ft. Area is converted to sq. ft. for reports (plotAreaSqFt).",
        },
        { key: "plotArea", type: "number", label: "Plot Area", placeholder: "E.g. 5000" },
        { key: "superAreaSqFt", type: "number", label: "Super Area (sq. ft.)", placeholder: "" },
        { key: "numberOfKeysRooms", type: "number", label: "Number of Keys (Rooms)", placeholder: "" },
        { key: "roomMix", type: "textarea", label: "Room Mix", placeholder: "" },
        { key: "totalFloorsInBuilding", type: "number", label: "Total Floors in Building", placeholder: "" },
        { key: "ageOfProperty", type: "text", label: "Age of Property", placeholder: "", showWhen: { key: "constructionStatus", value: "Ready to Move" } },
      ],
    },
    {
      sectionTitle: "Facilities & Amenities",
      fields: [
        { key: "keyFacilities", type: "textarea", label: "Key Facilities", placeholder: "" },
        { key: "numberOfRestaurants", type: "number", label: "Number of Restaurants", placeholder: "" },
        { key: "totalRestaurantSeating", type: "number", label: "Total Restaurant Seating", placeholder: "" },
        { key: "barLoungeCapacity", type: "number", label: "Bar/Lounge Capacity", placeholder: "" },
        { key: "numberOfBanquetHalls", type: "number", label: "Number of Banquet Halls", placeholder: "" },
        { key: "totalBanquetAreaSqFt", type: "number", label: "Total Banquet Area (sq. ft.)", placeholder: "" },
        { key: "maxBanquetCapacityPax", type: "number", label: "Max Banquet Capacity (pax)", placeholder: "" },
        { key: "numberOfMeetingRooms", type: "number", label: "Number of Meeting Rooms", placeholder: "" },
        { key: "totalCarParks", type: "number", label: "Total Car Parks", placeholder: "" },
      ],
    },
    {
      sectionTitle: "Operational & Financial Details",
      fields: [
        { key: "occupancyStatus", type: "dropdown", label: "Occupancy Status", options: occupancyStatusOptions, placeholder: "Leased / Pre-Rented", tooltip: "Defaulted to Leased / Pre-Rented." },
        { key: "provideOperationalData", type: "yesno", label: "Provide Operational Data?", options: YES_NO },
        { key: "averageOccupancyRatePercent", type: "number", label: "Average Occupancy Rate (%)", placeholder: "" },
        { key: "averageDailyRateAdr", type: "number", label: "Average Daily Rate (ADR)", placeholder: "" },
        { key: "totalSalePrice", type: "number", label: "Total Sale Price", placeholder: "e.g. 50000000", tooltip: "Enter the total expected sale price for the property." },
        { key: "pricePerSqFt", type: "number", label: "Price Per Sq. Ft. [Auto-Calculated]", placeholder: "e.g. 12000", tooltip: "System will calculate this. Formula: (Total Price / Total Built-up Area)." },
        { key: "pricePerKey", type: "number", label: "Price Per Key", placeholder: "e.g. 2500000", tooltip: "Enter price per room/key if applicable." },
      ],
    },
    {
      sectionTitle: "Legal & Licenses",
      fields: [
        { key: "titleDeed", type: "file", label: "Title Deed", tooltip: "Upload a scanned, clear copy of the property's Title Deed." },
        { key: "keyLicensesInPlace", type: "textarea", label: "Key Licenses in Place", placeholder: "" },
        { key: "approvalsCertificates", type: "textarea", label: "Approvals & Certificates", placeholder: "" },
      ],
    },
    {
      sectionTitle: "Media",
      fields: [
        { key: "propertyDescription", type: "richtext", label: "Property Description", tooltip: "Focus on logistical advantages: highway connectivity, port/airport proximity, tenant quality, and building specifications." },
        { key: "propertyImages", type: "file", label: "Property Images", tooltip: "Upload photos of the exterior, loading docks, interior (showing height), and truck access areas." },
        { key: "brandBrochureDeck", type: "text", label: "Brand Brochure / Deck", placeholder: "" },
      ],
    },
  ],
  "Hospitality (Vacant or Non-Operational)": [
    {
      sectionTitle: "Hotel Profile & Asset Details",
      fields: [
        { key: "proposedHotelType", type: "text", label: "Proposed Hotel Type", placeholder: "" },
        { key: "isPropertySecuredByBrand", type: "yesno", label: "Is the property secured by a brand?", options: YES_NO },
        { key: "brandName", type: "text", label: "Brand Name", placeholder: "" },
        { key: "proposedStarRating", type: "text", label: "Proposed Star Rating", placeholder: "" },
        {
          key: "plotAreaUnit",
          type: "dropdown",
          label: "Plot Area Unit",
          options: buildingPlotAreaUnitOptions,
          placeholder: "Unit",
          tooltip: "Default Sq. Ft. Area is converted to sq. ft. for reports (plotAreaSqFt).",
        },
        { key: "plotArea", type: "number", label: "Plot Area", placeholder: "E.g. 5000" },
        { key: "superAreaSqFt", type: "number", label: "Super Area (sq. ft.)", placeholder: "" },
        { key: "numberOfRooms", type: "number", label: "Number of Rooms", placeholder: "" },
        { key: "roomMix", type: "textarea", label: "Room Mix", placeholder: "" },
        { key: "totalFloorsInBuilding", type: "number", label: "Total Floors in Building", placeholder: "" },
        { key: "constructionStatus", type: "dropdown", label: "Construction Status", options: constructionStatusOptions },
        { key: "ageOfProperty", type: "text", label: "Age of Property", placeholder: "", showWhen: { key: "constructionStatus", value: "Ready to Move" } },
      ],
    },
    {
      sectionTitle: "Facilities & Amenities",
      fields: [
        { key: "keyFacilitiesAsPerDesign", type: "textarea", label: "Key Facilities (As per design)", placeholder: "" },
        { key: "numberOfProposedRestaurants", type: "number", label: "Number of Proposed Restaurants", placeholder: "" },
        { key: "banquetConferenceArea", type: "text", label: "Banquet / Conference Area", placeholder: "" },
        { key: "totalCarParks", type: "number", label: "Total Car Parks", placeholder: "" },
      ],
    },
    {
      sectionTitle: "Financial Details",
      fields: [
        { key: "occupancyStatus", type: "dropdown", label: "Occupancy Status", options: occupancyStatusOptions, placeholder: "Vacant", tooltip: "Defaulted to Vacant for this listing." },
        { key: "totalSalePrice", type: "number", label: "Total Price", placeholder: "e.g. 50000000", tooltip: "Enter the total expected sale price for the property. This is your listing price." },
        { key: "pricePerSqFt", type: "number", label: "Price Per Sq. Ft. [Auto-Calculated]", placeholder: "e.g. 12000", tooltip: "System will calculate this. Formula: (Total Price / Total Built-up Area)." },
        { key: "pricePerRoom", type: "number", label: "Price Per Room", placeholder: "e.g. 2500000", tooltip: "Enter price per room/key if applicable." },
      ],
    },
    {
      sectionTitle: "Legal & Approvals",
      fields: [
        { key: "titleDeed", type: "file", label: "Title Deed", tooltip: "Upload a scanned, clear copy of the property's Title Deed." },
        { key: "keyApprovalsInPlace", type: "textarea", label: "Key Approvals in Place", placeholder: "" },
        { key: "latestTaxReceipts", type: "text", label: "Latest Tax Receipts", placeholder: "" },
      ],
    },
    {
      sectionTitle: "Media",
      fields: [
        { key: "propertyDescription", type: "richtext", label: "Property Description", tooltip: "Focus on logistical advantages: highway connectivity, port/airport proximity, tenant quality, and building specifications." },
        { key: "propertyImages", type: "file", label: "Property Images", tooltip: "Upload photos of the exterior, loading docks, interior (showing height), and truck access areas." },
        { key: "videoTourLink", type: "text", label: "Video Tour Link", placeholder: "https://...", tooltip: "Provide a link to a video walkthrough of the property." },
        { key: "architecturalPlansBrochure", type: "text", label: "Architectural Plans / Brochure", placeholder: "" },
      ],
    },
  ],
}
